import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { clearUserInfo, hideIdleModal, hideLoginModal, logout } from "../../store/auth/authSlice";
import { getShorthandName, isValidUrl } from "../../utils/Helper";
import { useNavigate } from "react-router-dom";
import { ReactComponent as DownArrowEnt } from "../../assets/icons/downArrowEnt.svg";
import { setClearClaimDetails } from "../../store/claim/claimSlice";
import { useLogOutUserMutation } from "../../store/auth/usersApiSlice";
import { useGetIndividualQuery, useSetDefaultCompanyMutation } from "../../store/Company/companyApiSlice";
import { setActiveCompany, selectActiveCompany } from "../../store/activeCompanySlice";
import { defaultOrMostPrivileged } from "../../utils/companyStatus";
import { CompanySwitcherList } from "./CompanySwitcherList";

const UserProfileDropdown = ({ showDetails = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    userInfo: { name, profile_image, email },
    token
  } = useSelector((state) => state.auth);
  const [logOutUser] = useLogOutUserMutation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isValidProfileImage, setIsValidProfileImage] = useState(false);

  // TIP-487: the user's companies + the active "acting as" company.
  const userInfo = useSelector((state) => state.auth.userInfo);
  const activeCompany = useSelector(selectActiveCompany);
  const individualID = userInfo?.individual?.[0]?.individualID;
  const { data: individualData, refetch: refetchIndividual } = useGetIndividualQuery(
    { individualID, token },
    { skip: !individualID }
  );
  // TIP-499: the user's persisted default company (companyID string or null).
  const defaultCompanyId = individualData?.default_company || null;
  const [setDefaultCompany] = useSetDefaultCompanyMutation();
  const companies = (individualData?.individual || [])
    .map((i) => ({
      companyID: i?.companyID?.companyID,
      name: i?.companyID?.registered_company_name,
      profile_status: i?.profile_status,
    }))
    .filter((c) => c.companyID);

  // First load: honour the user's chosen default (TIP-499); otherwise fall back
  // to the auto most-privileged pick (TIP-487).
  useEffect(() => {
    if (activeCompany) return;
    const list = (individualData?.individual || [])
      .map((i) => ({
        companyID: i?.companyID?.companyID,
        name: i?.companyID?.registered_company_name,
        profile_status: i?.profile_status,
      }))
      .filter((c) => c.companyID);
    if (list.length > 0) {
      dispatch(setActiveCompany(defaultOrMostPrivileged(list, individualData?.default_company)));
    }
  }, [activeCompany, individualData, dispatch]);

  const handleSelectCompany = (company) => {
    dispatch(setActiveCompany(company));
    setIsOpen(false);
  };

  // TIP-499: persist the chosen default per-Individual, then refetch so the
  // switcher's star marker reflects the saved choice.
  const handleSetDefaultCompany = async (company) => {
    try {
      await setDefaultCompany({ companyID: company.companyID, token }).unwrap();
      refetchIndividual();
    } catch (err) {
      // non-fatal — leave the existing default in place on failure
    }
  };

  useEffect(() => {
    if (profile_image) {
      const isValid = isValidUrl(profile_image);
      setIsValidProfileImage(isValid);
    }
  }, [profile_image])

  const handleDropdownToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    await logOutUser({ token })
    // const res = await fetch(`${process.env.REACT_APP_K_HUB_URL}/api/auth/logout`, {
    //   method: "POST",
    //   credentials: "include", // 👈 VERY important
    // });

    // const data = await res?.json();
    dispatch(hideIdleModal());
    dispatch(logout());
    dispatch(hideLoginModal());
    dispatch(setClearClaimDetails())
    dispatch(clearUserInfo());
    navigate("/")
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="relative hidden sm:flex items-center gap-2 ml-6 cursor-pointer"
      ref={dropdownRef}
      onClick={handleDropdownToggle}
    >
      <button
        className="flex items-center focus:outline-none bg-white rounded-full border-4 w-12 h-12 border-profile shadow-sm justify-center"
      >
        {profile_image && isValidProfileImage ? (
          <img src={profile_image} className="rounded-full" alt="user" />
        ) : (
          <div className="rounded-full flex items-center justify-center shadow-md cursor-pointer">
            <span
              className={`text-profile ${getShorthandName(name || "").length > 2 ? "text-f12" : "text-f14"
                }`}
            >
              {getShorthandName(name || "")}
            </span>
          </div>
        )}
      </button>

      {showDetails && (
        <div className="flex gap-2 items-center">
          <div className="text-left sm:hidden md:block">
            <p className="text-[14px] text-[#3C4848]">{name}</p>
            {/* TIP-487: navbar shows the active "acting as" company */}
            {activeCompany ? (
              <p className="text-[12px] text-[#777F7F] truncate max-w-[160px]">{activeCompany.name}</p>
            ) : (
              <p className="text-[12px] text-[#777F7F]">{email}</p>
            )}
          </div>
          <DownArrowEnt />
        </div>
      )}

      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          /* TIP-506 — anchor to the right edge in BOTH the dashboard and the
             public nav so the panel never runs off-screen (was left-0 when
             showDetails, which overflowed right in the dashboard). */
          className="absolute right-0 top-full mt-3 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 text-left z-50 overflow-hidden"
        >
          {/* Header — avatar + name + email, links to profile */}
          <button
            onClick={() => navigate('/dashboard/profile')}
            className="flex items-center gap-3 w-full px-4 py-3.5 text-left hover:bg-gray-50 border-b border-gray-100"
          >
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FFEDE6] text-[#FB512D] font-semibold text-f14 shrink-0">
              {getShorthandName(name || "")}
            </span>
            <span className="min-w-0">
              <span className="block text-f14 font-semibold text-[#012234] truncate">{name}</span>
              <span className="block text-f12 text-[#777F7F] truncate">{email}</span>
            </span>
          </button>

          {/* TIP-487/506: "Using TREVEX for" company switcher */}
          {companies.length > 0 && (
            <div className="border-b border-gray-100">
              <CompanySwitcherList
                companies={companies}
                activeCompanyId={activeCompany?.companyID}
                defaultCompanyId={defaultCompanyId}
                onSelect={handleSelectCompany}
                onSetDefault={handleSetDefaultCompany}
              />
            </div>
          )}

          <Link
            to="/dashboard"
            className="flex items-center gap-2.5 px-4 py-2.5 text-f14 text-[#012234] hover:bg-gray-50"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
              <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
              <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
              <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.4" />
            </svg>
            Dashboard
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-f14 text-[#D92D20] hover:bg-[#FEF3F2] text-left"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 14H3.5A1.5 1.5 0 012 12.5v-9A1.5 1.5 0 013.5 2H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <path d="M10.5 11L14 8l-3.5-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Log out
          </button>
        </div>
      )}
    </div>

  );
};

export default UserProfileDropdown;
